// Today and the Price map act on the same 2 jobs: a payment that opened nothing (paid_no_access,
// raised the moment Stripe's event lands) and access with no payment behind it and no reason
// (access_no_payment, the morning check). This is the 1 model both screens use, so each job says the
// same thing in both places and its buttons do the same: the words follow the job's cause (S05), and so
// do the buttons (the stage 5 critic's wrong_or_contradictory 9). Pure: it imports only the date
// helpers, so admin-today.spec.js and admin-prices.spec.js check it on its own.
//
// What each button does, on either screen (data-model 10.4):
//   map       the owner maps the price on the Price map (Today links there), then the held payment is applied
//   apply     resolve-payment {flag_id}: the held payment applied again, once its price is on the map
//   create    resolve-payment {flag_id} (or {flag_id, email} when the checkout had no email): a new member
//   match     resolve-payment {flag_id, person_id}: applied to someone already in the hub
//   handled   the job ticked off with the resolution "Marked as handled", and nothing applied
//   reason    the reason on the access row, then the job ticked off with "Reason added"
//   revoke    revoke-access {access_id, end_reason: 'revoked_by_staff'}, then ticked off with "Access ended"
// Adam's writing rules hold: numerals, no dashes, contractions, and a person is named, never "they".
import { realFirstName } from '../lib/names.js'
import { uk, time } from '../lib/format.js'

export const MISMATCH_KINDS = ['paid_no_access', 'access_no_payment']

export const MISMATCH_WORDS = {
  kindPaid: 'Paid, but no access',
  kindNoPayment: 'Access, but no payment',
  map: 'Map this price',
  openMap: 'Open the price map',
  ownerMaps: 'Only an owner can map a price.',
  apply: 'Apply the payment',
  create: 'Create the member',
  creating: 'Creating',
  match: 'Match to someone',
  handled: 'Mark as handled',
  handledAsk: 'Mark this as handled? It comes off Today and the Price map, and nothing is applied.',
  handledDone: 'Marked as handled. Nothing was applied.',
  keepIt: 'Keep it',
  cancel: 'Cancel',
  email: "The new member's email",
  emailHint: 'This checkout has no email, so the hub needs 1 to make the member. The access email goes to this address.',
  emailWrong: 'Enter a valid email address.',
  search: 'Find someone by name or email',
  nobody: 'Nobody in the hub matches that.',
  addReason: 'Add a reason',
  saveReason: 'Save the reason',
  saving: 'Saving',
  reasonNeeded: 'Add a reason first. Only staff see it.',
  revoke: 'Revoke',
  revokeAccess: 'Revoke access',
}

// What each button ticks the job off with (flags.resolution), the same from either screen
export const RESOLUTION = { reason: 'Reason added', revoked: 'Access ended', handled: 'Marked as handled' }

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
export const emailOk = e => EMAIL.test(String(e || '').trim())

const MONTHS3 = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
// 29 Sep, or 29 Sep 2025 when it isn't this year
function dayOf(t, nowMs) {
  const a = uk(t)
  const day = `${a.d} ${MONTHS3[a.m - 1]}`
  return a.y === uk(nowMs).y ? day : `${day} ${a.y}`
}

// Sam, else the full name, else the email (never a pronoun)
// (the database's "there", a name that gave none, isn't a first name here: lib/names.js)
export const firstOf = job => realFirstName(job.person_first_name) || job.person_full_name || job.person_email || 'This person'
export const fullOf = job => job.person_full_name || job.person_email || 'Someone'
// in a sentence: "the masterclass" (its full name carries the 1 word the hub keeps for its name alone),
// else the product's name. The masterclass is the video at /masterclass, by its slug, or the type where
// no slug came with the row; another masterclass (the product-types plan) goes by its own name.
export function productIn(job) {
  const slug = job.product_slug || job.slug || null
  if (slug === 'masterclass' || (!slug && (job.product_type || job.type) === 'masterclass')) return 'the masterclass'
  return job.product_name || 'the product'
}

// What a ticked job says beside it, on Today and the Price map alike: the database's resolution, and
// who ticked it at what time ("Reason added. Ticked off by Test owner at 9am.")
export function doneStamp(job) {
  if (job.status !== 'done') return ''
  const who = job.done_by_name ? `Ticked off by ${job.done_by_name}` : ''
  const at = job.done_at ? `at ${time(job.done_at)}` : ''
  const stamp = [who, who && at].filter(Boolean).join(' ')
  const res = job.resolution ? `${job.resolution.replace(/[.]$/, '')}.` : ''
  if (res && stamp) return `${res} ${stamp}.`
  if (res) return res
  return stamp ? `${stamp}.` : 'Done.'
}

// ---- Paid, but no access ----------------------------------------------------------------------------

// The card's words by the job's reason. amount: stripe-prices' display ("£97+vat"), or null
export function paidText(job, amount, nowMs = Date.now()) {
  const d = job.detail || {}
  const who = d.email || job.person_email || job.person_full_name || 'Someone'
  const on = job.raised_at ? ` on ${dayOf(job.raised_at, nowMs)}` : ''
  const paid = `${who} paid${amount ? ` ${amount}` : ''}${on}`
  switch (d.reason) {
    case 'unmapped_price': return `${paid} on a price that isn't in the price map, so nothing opened.`
    case 'no_email': return `Someone paid${amount ? ` ${amount}` : ''}${on}, and Stripe gave no email, so nothing opened.`
    case 'processing_failed': return `${paid}, but the hub couldn't apply it${d.attempts ? ` after ${d.attempts} tries` : ''}, so nothing opened.`
    case 'payment_waiting_over_a_day':
      return `${paid}, and the payment has waited over a day for its ${d.waiting_for === 'person' ? 'member' : 'checkout'}, so nothing opened.`
    case 'balance_with_no_place': return `${paid} for a balance, with no place secured to pay it on, so nothing opened.`
    case 'nothing_paid': return `${who} checked out${on}, but nothing was paid, so nothing opened. Give access by hand if it should open.`
    case 'paid_after_access_ended': return `${paid} for ${productIn(job)}, after that access had ended, so nothing opened.`
    default: return `${paid}, and nothing opened.`
  }
}

// The buttons a Paid, but no access card has, in order, by its reason and the caller's role (owner and
// admin; nobody else acts on it). mapped: true once every price the payment held is on the map, false
// while 1 isn't, null where the screen doesn't read the map (Today, which links to the Price map
// instead, and whose Apply the payment says why nothing changed until the price is mapped).
export function paidActions(job, role, { mapped = null } = {}) {
  if (role !== 'owner' && role !== 'admin') return []
  switch (job.detail?.reason) {
    case 'unmapped_price':
      if (mapped === false) return role === 'owner' ? ['map', 'handled'] : ['handled']
      return ['apply', 'handled']
    case 'no_email':
    case 'payment_waiting_over_a_day':
    case 'processing_failed':
      return ['create', 'match']
    case 'balance_with_no_place': return ['match', 'handled']
    // nothing_paid, a payment after the access ended, and anything else: there's nothing to apply
    default: return ['handled']
  }
}

// Where Today sends an unmapped price: the Price map, to map it (owner) or see it (admin)
export function mapLink(role) {
  return { label: role === 'owner' ? MISMATCH_WORDS.map : MISMATCH_WORDS.openMap, to: '/admin/prices' }
}

// The prices a Paid, but no access card's checkout held that aren't on the map
export function unmappedPricesOf(job) {
  const prices = job.detail?.prices
  if (!prices || typeof prices !== 'object') return []
  return Object.entries(prices).filter(([, v]) => v === 'unmapped').map(([k]) => k)
}

// Create the member asks for an email only when the checkout had none
export const createNeedsEmail = job => !job.detail?.email

// ---- Access, but no payment ---------------------------------------------------------------------------

export function noPaymentText(job) {
  return `${fullOf(job)} has ${productIn(job)} with no payment in Stripe and no reason logged.`
}

export function reasonLabel(job) { return `Why ${firstOf(job)} has it` }
export function reasonHint(job) { return `For example, paid by bank transfer. Only staff see it, on ${firstOf(job)}'s page.` }

// "Reason added: paid by bank transfer. It won't show here again." (AdminPrices.dc.html's done line)
export function reasonDone(reason) {
  const r = String(reason || '').trim().replace(/\.$/, '')
  const lower = /^[A-Z][a-z]/.test(r) ? r[0].toLowerCase() + r.slice(1) : r
  return `Reason added: ${lower}. It won't show here again.`
}

export function revokeAsk(job) {
  const first = firstOf(job)
  return `Revoke ${productIn(job)} for ${fullOf(job)}? ${first} loses access straight away, and ${first}'s progress is kept.`
}
export function revokeDone(job) { return `Revoked. ${firstOf(job)}'s progress is kept.` }
